import { graphql, useLazyLoadQuery } from 'react-relay';

import {
	templateInitialSearchResultsContentfulRelayQuery,
	templateInitialSearchResultsContentfulRelayQuery$variables,
} from '@/__generated_contentful__/templateInitialSearchResultsContentfulRelayQuery.graphql';

import { CategoryTemplateOrder } from '../../../config/category-template-order';
import { DEFAULT_CATEGORY_SLUG, parseCategorySlug } from '../../hooks/use-category-slug';

import { CategorySearchSuggestion } from './template-category-search-results-contentful-relay';
import { TemplateSearchSuggestion } from './template-fuzzy-search-results-contentful-relay';
import { TemplatePaginatedSearchResults } from './template-paginated-search-results-contentful-relay';

export const TemplateInitialSearchResultsRelayQuery = graphql`
	query templateInitialSearchResultsContentfulRelayQuery(
		$urlSlugs: [String!]!
		$templateIds: [String!]!
		$categoryUrlSlugs: [String!]!
		$limit: Int!
		$skip: Int!
		$locale: String!
	) {
		...templatePaginatedSearchResultsContentfulRelayRefetchableFragment
			@alias(as: "initialSearchResults")
			@arguments(
				urlSlugs: $urlSlugs
				templateIds: $templateIds
				categoryUrlSlugs: $categoryUrlSlugs
				limit: $limit
				skip: $skip
				locale: $locale
			)
	}
`;

type TemplateId = string;

function buildWhereClause(
	value?: TemplateSearchSuggestion | CategorySearchSuggestion | TemplateId[] | null,
): Record<string, string[]> {
	const filters: Record<string, string[]> = {
		templateIds: [],
		urlSlugs: [],
		categoryUrlSlugs: [],
	};

	if (Array.isArray(value) && value.length < 1) {
		// An empty array of templateIds indicates that we received no recomendations
		// from the service, so we should fall back to showing the default category.
		value = null;
	}

	if (Array.isArray(value)) {
		filters['templateIds'] = value;
	} else if (value?.type === 'template' && value.id) {
		filters['urlSlugs'] = [value.id];
	} else {
		const categorySlug = value?.type === 'category' ? value.id : DEFAULT_CATEGORY_SLUG;
		const orderedTemplates =
			CategoryTemplateOrder[categorySlug as keyof typeof CategoryTemplateOrder];
		if (orderedTemplates) {
			filters['urlSlugs'] = Array.from(Object.keys(orderedTemplates));
		} else {
			filters['categoryUrlSlugs'] = [categorySlug];
		}
	}

	return filters;
}

function isLocation(
	value:
		| string
		| CategorySearchSuggestion
		| TemplateSearchSuggestion
		| TemplateId[]
		| Location
		| null,
): value is Location {
	return (
		value != null &&
		typeof value !== 'string' &&
		!Array.isArray(value) &&
		!(value as CategorySearchSuggestion).type
	);
}

export type SearchInput = {
	first?: number | null | undefined;
	locale?: string | null | undefined;
	templateIds?: TemplateId[] | null | undefined;
};

export function parseSearchQuery(
	searchTerm: CategorySearchSuggestion | TemplateSearchSuggestion | TemplateId[] | Location | null,
): {
	first: number;
	whereClause: Record<string, string[]>;
} {
	let whereClause: Record<string, string[]>;
	if (isLocation(searchTerm)) {
		const id = parseCategorySlug(searchTerm.pathname);
		whereClause = buildWhereClause(id ? { type: 'category', id } : null);
	} else {
		whereClause = buildWhereClause(searchTerm);
	}
	let first = 9;

	// if the whereClause has conditions that amount to more than `first`,
	// we need to set the first to the necessary count to fullfill the where clause.
	if (whereClause['templateIds']) {
		first = Math.max(first, whereClause['templateIds'].length);
	}
	if (whereClause['urlSlugs']) {
		first = Math.max(first, whereClause['urlSlugs'].length);
	}
	if (whereClause['categoryUrlSlugs']) {
		first = Math.max(first, whereClause['categoryUrlSlugs'].length);
	}

	return {
		first,
		whereClause,
	};
}

type SortLookup = {
	[templateUrlSlug: string]: number;
};

export type TemplateInitialSearchResultsProps = {
	urlSlugs: string[];
	templateIds: string[];
	categoryUrlSlugs: string[];
	limit: templateInitialSearchResultsContentfulRelayQuery$variables['limit'];
	skip: templateInitialSearchResultsContentfulRelayQuery$variables['skip'];
	locale: templateInitialSearchResultsContentfulRelayQuery$variables['locale'];
	/**
	 * A unique key to identify the search term.
	 * This is required to force a new instance
	 * of `TemplatePaginatedSearchResults`
	 * when the search term changes.
	 */
	searchKey: string;
	sortLookup?: 'templateNameExternal' | SortLookup;
};

export const TemplateInitialSearchResults = ({
	urlSlugs,
	templateIds,
	categoryUrlSlugs,
	limit,
	skip,
	locale,
	searchKey,
	sortLookup,
}: TemplateInitialSearchResultsProps) => {
	const { initialSearchResults } =
		useLazyLoadQuery<templateInitialSearchResultsContentfulRelayQuery>(
			TemplateInitialSearchResultsRelayQuery,
			{ urlSlugs, templateIds, categoryUrlSlugs, limit, skip, locale },
		);

	return (
		<TemplatePaginatedSearchResults
			key={searchKey}
			initialSearchResults={initialSearchResults}
			urlSlugs={[...urlSlugs]}
			templateIds={[...templateIds]}
			categoryUrlSlugs={[...categoryUrlSlugs]}
			limit={limit}
			skip={skip}
			locale={locale}
			sortLookup={sortLookup}
		/>
	);
};
